'use client';

import { type FC, useContext } from 'react';
import { usePathname, useParams } from 'next/navigation';
import { LeftNavItem, type LeftNavItemProps } from '@/_components/LeftNavItem';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { AdminConversationsContext } from '@/_contexts/admin/AdminConversationsProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const DefaultAdvisorNav: FC = () => {
  const path = usePathname();
  const { companyHash } = useParams();
  const { preferences } = useContext(FirmSettingsContext);
  const { unseenCount } = useContext(AdminConversationsContext);
  const { permissions, isPartner } = useContext(UserContext);

  const hasQuestionnaireTemplateViewPermissions: boolean = getHasPermission(
    PermissionsMapEnum['questionnaire_template.view'],
    permissions,
  );

  const isTemplateEditorDisabled: boolean = Boolean(
    !isPartner && !!preferences?.template_disabled_for_nonadmin,
  );

  const items: Array<LeftNavItemProps> = [
    {
      name: 'Collect',
      icon: 'circles-three',
      href: '/',
      isActive: path === '/' || path === '/overview',
    },
    ...(hasQuestionnaireTemplateViewPermissions && !isTemplateEditorDisabled
      ? [
          {
            name: 'Templates',
            icon: 'file',
            href: '/templates',
            isActive: path === '/templates',
          } as LeftNavItemProps,
        ]
      : []),
    { isSeparator: true },
    {
      name: 'Entities',
      isActive: path === '/admin',
      href: '/admin',
      icon: 'buildings',
    },
    {
      name: 'Users',
      isActive: path === '/clients',
      href: '/clients',
      icon: 'users',
    },
    { isSeparator: true },
    ...(!preferences?.communication_disabled
      ? [
          {
            name: 'Messages',
            icon: 'chats',
            href: '/messages',
            notificationCount: unseenCount,
            isActive:
              path === '/messages' ||
              path.startsWith(`/admin/${companyHash}/messages`),
          } as LeftNavItemProps,
        ]
      : []),
    ...(preferences?.new_tax_return_delivery_enabled
      ? [
          {
            name: 'Deliver',
            icon: 'scroll',
            href: '/tax-returns',
            isActive: path === '/tax-returns',
          } as LeftNavItemProps,
        ]
      : preferences?._tmp_enterprise_tier
        ? [
            {
              name: 'Deliver',
              icon: 'scroll',
              href: '/enterprise/tax-returns',
              isLocked: true,
              isActive: path === '/enterprise/tax-returns',
            } as LeftNavItemProps,
          ]
        : []),
    ...(preferences?.review_and_export_enabled
      ? [
          {
            name: 'Connect',
            icon: 'plugs',
            href: '/review-and-export',
            isActive: path.startsWith('/review-and-export'),
          } as LeftNavItemProps,
        ]
      : preferences?._tmp_enterprise_tier
        ? [
            {
              name: 'Connect',
              icon: 'plugs',
              isLocked: true,
              href: '/enterprise/review-and-export',
              isActive: path.startsWith('/enterprise/review-and-export'),
            } as LeftNavItemProps,
          ]
        : []),
    ...(preferences?.ai_chat_enabled
      ? []
      : preferences?._tmp_enterprise_tier
        ? [
            {
              name: 'Admin Assist',
              icon: 'chat',
              isLocked: true,
              href: '/enterprise/admin-assist',
              isActive: path.startsWith('/enterprise/admin-assist'),
            } as LeftNavItemProps,
          ]
        : []),
    ...(preferences?.signature_requests_enabled
      ? [
          {
            name: 'Signatures',
            icon: 'signature',
            href: '/signature-requests',
            isActive: path.startsWith('/signature-requests'),
          } as LeftNavItemProps,
        ]
      : []),
    ...(preferences?._tmp_enterprise_tier
      ? [
          {
            name: 'Insights',
            icon: 'spinner',
            href: '/insights/activities',
            isActive: path.startsWith('/insights'),
          } as LeftNavItemProps,
        ]
      : preferences?.all_activities_enabled
        ? [
            {
              name: 'Activities',
              icon: 'pulse',
              href: '/activities',
              isActive: path.startsWith('/activities'),
            } as LeftNavItemProps,
          ]
        : []),
    ...(preferences?._tmp_enterprise_tier || preferences?.binders_enabled
      ? [
          {
            name: 'Binders',
            icon: 'folder-user',
            isLocked: !preferences?.binders_enabled,
            href: '/binders',
            isActive: path.startsWith('/binders'),
          } as LeftNavItemProps,
        ]
      : []),
    ...(preferences?._tmp_enterprise_tier
      ? [
          {
            name: 'Developer',
            icon: 'code',
            href: '/developer/api-docs',
            isActive: path.startsWith('/developer'),
          } as LeftNavItemProps,
        ]
      : []),
  ];

  return (
    <>
      {items.map((item, key) => (
        <LeftNavItem {...item} key={key} isActive={item.isActive} />
      ))}
    </>
  );
};
